import React from 'react';
import Panel from '../../components/ui/Panel';
import Button from '../../components/ui/Button';
import ComponentShowcase from '../../components/ui/ComponentShowcase';
import Tooltip from '../../components/ui/Tooltip';

const basicCode = `<Tooltip title="Rune scimitar" content="A vicious curved sword.">
  <Button>Hover</Button>
</Tooltip>`;

const placementCode = `<Tooltip placement="top" content="Above">Top</Tooltip>
<Tooltip placement="bottom" content="Below">Bottom</Tooltip>
<Tooltip placement="left" content="Left side">Left</Tooltip>
<Tooltip placement="right" content="Right side">Right</Tooltip>`;

const TooltipPage: React.FC = () => {
  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Tooltip</h1>
      <p className="text-osrs-yellow mb-8">
        Hovering an item shows a black box. The name is yellow. The examine line is white. Focus shows the same box.
      </p>

      <section id="examples" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Examples</h2>
        <ComponentShowcase
          id="basic"
          title="Item tooltip"
          description="Hover or focus the button."
          component={
            <Tooltip title="Rune scimitar" content="A vicious curved sword.">
              <Button>Hover</Button>
            </Tooltip>
          }
          code={basicCode}
        />
      </section>

      <section id="positioning" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Positioning</h2>
        <ComponentShowcase
          id="positioning-examples"
          title="Placements"
          description="Top, bottom, left, and right."
          component={
            <div className="flex flex-wrap gap-8 py-8 justify-center">
              <Tooltip placement="top" content="Above"><Button variant="secondary">Top</Button></Tooltip>
              <Tooltip placement="bottom" content="Below"><Button variant="secondary">Bottom</Button></Tooltip>
              <Tooltip placement="left" content="Left side"><Button variant="secondary">Left</Button></Tooltip>
              <Tooltip placement="right" content="Right side"><Button variant="secondary">Right</Button></Tooltip>
            </div>
          }
          code={placementCode}
        />
      </section>

      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        <Panel>
          <h3 id="when-to-use" className="text-xl text-osrs-gold font-bold mb-3">When to Use</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use for an item name and its examine text.</li>
            <li>Keep the title short. Put the longer line in content.</li>
            <li>Do not put actions inside a tooltip. It does not receive clicks.</li>
          </ul>
        </Panel>
      </section>

      <section id="api-reference" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">API Reference</h2>
        <Panel>
          <table className="w-full text-left text-osrs-yellow">
            <thead>
              <tr className="text-osrs-gold">
                <th className="py-2 px-4">Prop</th>
                <th className="py-2 px-4">Type</th>
                <th className="py-2 px-4">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4">title</td>
                <td className="py-2 px-4">string</td>
                <td className="py-2 px-4">Yellow name line</td>
              </tr>
              <tr>
                <td className="py-2 px-4">content</td>
                <td className="py-2 px-4">node</td>
                <td className="py-2 px-4">White body line</td>
              </tr>
              <tr>
                <td className="py-2 px-4">placement</td>
                <td className="py-2 px-4">top | bottom | left | right</td>
                <td className="py-2 px-4">Where the box sits. Default top.</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </section>
    </div>
  );
};

export default TooltipPage;
